import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import { renderAsync } from 'docx-preview';
import Swal from 'sweetalert2';
import axiosInstance from '../utils/axiosInstance';
import {
  ACCEPT,
  DOCUMENTS_API,
  MAX_UPLOAD_BYTES,
  downloadVersion,
  downloadZip,
  extOf,
  fetchVersionBlob,
  kindOf,
  stripExt,
} from '../utils/tvecDocuments';
import { formatSize } from '../utils/tvecTemplates';
import { Field, Modal, inputClass } from './TvecForm';
import { SheetTable, parseSheet } from './TvecSheetTable';

const KIND_BADGE = {
  image: { label: 'IMG', style: 'bg-violet-50 text-violet-700' },
  pdf: { label: 'PDF', style: 'bg-rose-50 text-rose-700' },
  word: { label: 'DOC', style: 'bg-sky-50 text-sky-700' },
  excel: { label: 'XLS', style: 'bg-emerald-50 text-emerald-700' },
};

const formatWhen = (value) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const errorText = (err, fallback) => err?.response?.data?.message || fallback;

function KindBadge({ fileName }) {
  const badge = KIND_BADGE[kindOf(fileName)] || { label: 'FILE', style: 'bg-slate-100 text-slate-600' };
  return <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold ${badge.style}`}>{badge.label}</span>;
}

// ---- Preview ---------------------------------------------------------------

function PreviewBody({ version }) {
  const kind = kindOf(version.originalName);
  const ext = extOf(version.originalName);
  const hostRef = useRef(null);
  const [state, setState] = useState('loading'); // loading | ready | error | unsupported
  const [objectUrl, setObjectUrl] = useState('');
  const [sheets, setSheets] = useState(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let created = '';
    setState('loading');
    setObjectUrl('');
    setSheets(null);
    setActive(0);

    if (kind === 'word' && ext === 'doc') {
      setState('unsupported');
      return undefined;
    }

    (async () => {
      try {
        const blob = await fetchVersionBlob(version.id);
        if (cancelled) return;
        if (kind === 'image' || kind === 'pdf') {
          created = URL.createObjectURL(blob);
          setObjectUrl(created);
        } else if (kind === 'excel') {
          const wb = XLSX.read(await blob.arrayBuffer(), { type: 'array' });
          if (cancelled) return;
          setSheets(wb.SheetNames.map((name) => parseSheet(name, wb.Sheets[name])));
        } else if (kind === 'word') {
          const buf = await blob.arrayBuffer();
          if (cancelled || !hostRef.current) return;
          hostRef.current.innerHTML = '';
          await renderAsync(buf, hostRef.current, undefined, { className: 'docx', inWrapper: true, breakPages: true, ignoreLastRenderedPageBreak: true });
        }
        if (!cancelled) setState('ready');
      } catch (err) {
        if (!cancelled) setState('error');
      }
    })();

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [version.id, kind, ext]);

  if (state === 'unsupported') {
    return <p className="py-10 text-center text-sm text-slate-600">Old .doc files can’t be previewed. Use Download to open it in Word.</p>;
  }
  const current = sheets ? sheets[active] || sheets[0] : null;
  return (
    <div>
      {state === 'loading' ? <p className="py-10 text-center text-sm text-slate-500">Loading preview…</p> : null}
      {state === 'error' ? <p className="py-10 text-center text-sm text-rose-600">Could not preview this file. Use Download to open it.</p> : null}
      {kind === 'image' && objectUrl ? <img src={objectUrl} alt={version.originalName} className="mx-auto max-h-[72vh] max-w-full rounded-lg object-contain" /> : null}
      {kind === 'pdf' && objectUrl ? <iframe title={version.originalName} src={objectUrl} className="h-[72vh] w-full rounded-lg border border-slate-200 bg-white" /> : null}
      {kind === 'excel' && current ? (
        <div>
          {sheets.length > 1 ? (
            <div className="mb-4 flex flex-wrap gap-2 border-b border-slate-200 pb-3">
              {sheets.map((sheet, i) => (
                <button key={sheet.name} type="button" onClick={() => setActive(i)} className={`rounded-lg px-4 py-1.5 text-xs font-semibold transition ${i === active ? 'bg-sky-600 text-white' : 'border border-slate-200 text-slate-600 hover:bg-slate-100'}`}>
                  {sheet.name}
                </button>
              ))}
            </div>
          ) : null}
          <SheetTable sheet={current} headerRows={1} legacy={false} />
        </div>
      ) : null}
      <div ref={hostRef} className="overflow-x-auto" style={{ display: kind === 'word' && state !== 'error' ? 'block' : 'none' }} />
    </div>
  );
}

function PreviewModal({ title, version, onClose }) {
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const download = async () => {
    setBusy(true);
    try { await downloadVersion(version); } catch (err) { Swal.fire({ icon: 'error', title: 'Could not download', text: errorText(err, 'Please try again.') }); }
    setBusy(false);
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 px-3 py-6" onClick={onClose}>
      <div className="flex max-h-full w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold text-slate-900">{title}</h3>
            <p className="truncate text-xs text-slate-500">{version.originalName} · {formatSize(version.size)} · {formatWhen(version.uploadedAt)}</p>
          </div>
          <div className="flex shrink-0 gap-2">
            <button type="button" onClick={download} disabled={busy} className="rounded-lg bg-sky-600 px-4 py-2 text-xs font-semibold text-white hover:bg-sky-700 disabled:bg-sky-400">
              {busy ? 'Downloading…' : 'Download'}
            </button>
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100">Close</button>
          </div>
        </div>
        <div className="overflow-auto bg-slate-50 p-5">
          <PreviewBody version={version} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---- Upload ----------------------------------------------------------------

// mode "new": a new document (name + description). mode "version": re-upload of `doc`.
function UploadModal({ section, mode, doc, onClose, onSaved }) {
  const isVersion = mode === 'version';
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState({});
  const [progress, setProgress] = useState(null);
  const [dragging, setDragging] = useState(false);
  const saving = progress !== null;

  const pick = (picked) => {
    if (!picked) return;
    if (!kindOf(picked.name)) { setErrors({ file: 'Only images, PDF, Word and Excel files can be uploaded.' }); return; }
    if (picked.size > MAX_UPLOAD_BYTES) { setErrors({ file: 'The file is too large (25 MB maximum).' }); return; }
    if (picked.size === 0) { setErrors({ file: 'That file is empty.' }); return; }
    setFile(picked);
    setErrors({});
    // The name starts as the file name until the user types their own.
    if (!isVersion && !nameTouched) setName(stripExt(picked.name));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const next = {};
    if (!file) next.file = 'Choose a file to upload.';
    if (!isVersion && !name.trim()) next.name = 'Enter a name.';
    if (Object.keys(next).length) { setErrors(next); return; }

    const body = new FormData();
    body.append('file', file);
    if (!isVersion) {
      body.append('section', section);
      body.append('name', name.trim());
      body.append('description', description.trim());
    }
    setProgress(0);
    try {
      const res = await axiosInstance.post(isVersion ? `${DOCUMENTS_API}/${doc.id}/versions` : DOCUMENTS_API, body, {
        onUploadProgress: (ev) => { if (ev.total) setProgress(Math.min(99, Math.round((ev.loaded / ev.total) * 100))); },
      });
      onSaved(res.data.data);
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors) setErrors(data.errors);
      else Swal.fire({ icon: 'error', title: 'Upload failed', text: data?.message || 'Please try again.' });
      setProgress(null);
    }
  };

  return (
    <Modal
      title={isVersion ? 'Upload a new version' : 'Upload document'}
      subtitle={isVersion ? `“${doc.name}” — the new file becomes the current one; earlier files stay in the history.` : 'Image, PDF, Word or Excel file, up to 25 MB.'}
      onClose={saving ? () => {} : onClose}
      onSubmit={submit}
      saving={saving}
      submitLabel={isVersion ? 'Upload version' : 'Upload'}
    >
      <Field label="File" required error={errors.file}>
        <label
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files?.[0]); }}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-6 text-center transition ${dragging ? 'border-sky-500 bg-sky-50' : errors.file ? 'border-rose-300 bg-rose-50/40' : 'border-slate-300 bg-slate-50 hover:border-sky-400 hover:bg-sky-50/50'}`}
        >
          <input type="file" accept={ACCEPT} className="sr-only" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
          {file ? (
            <span className="flex items-center gap-3 text-left">
              <KindBadge fileName={file.name} />
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-slate-900">{file.name}</span>
                <span className="block text-xs text-slate-500">{formatSize(file.size)} · click to choose a different file</span>
              </span>
            </span>
          ) : (
            <>
              <span className="text-sm font-semibold text-slate-700">Click to choose a file, or drop it here</span>
              <span className="mt-1 text-xs text-slate-500">JPG, PNG, GIF, WEBP, PDF, DOC, DOCX, XLS, XLSX</span>
            </>
          )}
        </label>
      </Field>
      {!isVersion ? (
        <>
          <Field label="Name" required error={errors.name} hint="Filled from the file name — change it if you like.">
            <input type="text" value={name} maxLength={200} onChange={(e) => { setName(e.target.value); setNameTouched(true); setErrors((p) => ({ ...p, name: undefined })); }} className={inputClass(errors.name)} />
          </Field>
          <Field label="Description" hint="Optional">
            <textarea rows={3} maxLength={1000} value={description} onChange={(e) => setDescription(e.target.value)} className={`${inputClass(false)} resize-none`} />
          </Field>
        </>
      ) : null}
      {saving ? (
        <div role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} className="h-2 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full bg-sky-600 transition-all" style={{ width: `${progress}%` }} />
        </div>
      ) : null}
    </Modal>
  );
}

function EditModal({ doc, onClose, onSaved }) {
  const [name, setName] = useState(doc.name);
  const [description, setDescription] = useState(doc.description || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!name.trim()) { setError('Enter a name.'); return; }
    setSaving(true);
    try {
      const res = await axiosInstance.put(`${DOCUMENTS_API}/${doc.id}`, { name: name.trim(), description: description.trim() });
      onSaved(res.data.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not save', text: errorText(err, 'Please try again.') });
      setSaving(false);
    }
  };

  return (
    <Modal title="Edit document" onClose={onClose} onSubmit={submit} saving={saving} submitLabel="Save changes">
      <Field label="Name" required error={error}>
        <input autoFocus type="text" value={name} maxLength={200} onChange={(e) => { setName(e.target.value); setError(''); }} className={inputClass(error)} />
      </Field>
      <Field label="Description" hint="Optional">
        <textarea rows={3} maxLength={1000} value={description} onChange={(e) => setDescription(e.target.value)} className={`${inputClass(false)} resize-none`} />
      </Field>
    </Modal>
  );
}

// ---- Panel -----------------------------------------------------------------

const btn = 'rounded-lg border px-3 py-1.5 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50';
const btnNeutral = `${btn} border-slate-200 text-slate-600 hover:bg-slate-100`;
const btnPrimary = `${btn} border-sky-600 bg-sky-600 text-white hover:bg-sky-700`;
const btnDanger = `${btn} border-rose-200 text-rose-600 hover:bg-rose-50`;

// heading / intro: wording for the panel. disabledReason: when set, new uploads are blocked
// and the reason is shown. onCountChange: told how many documents there are.
export default function TvecDocuments({ section, sectionTitle, heading = 'Documents', intro, disabledReason, onCountChange }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [upload, setUpload] = useState(null); // { mode, doc? }
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null); // { title, version }
  const [openHistory, setOpenHistory] = useState(() => new Set());
  const [busy, setBusy] = useState(''); // what is being downloaded right now

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axiosInstance
      .get(DOCUMENTS_API, { params: { section } })
      .then((res) => { if (!cancelled) setDocs(res?.data?.data ?? []); })
      .catch(() => { if (!cancelled) setLoadError('Failed to load documents.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [section]);

  // Kept in a ref so a new callback from the parent does not re-trigger this.
  const countCallback = useRef(onCountChange);
  countCallback.current = onCountChange;
  useEffect(() => {
    if (!loading && countCallback.current) countCallback.current(docs.length);
  }, [docs.length, loading]);

  // Newest activity first.
  const upsert = useCallback((doc) => {
    setDocs((prev) => [doc, ...prev.filter((d) => d.id !== doc.id)]);
  }, []);

  const toggleHistory = (id) => setOpenHistory((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const run = async (key, task) => {
    if (busy) return;
    setBusy(key);
    try { await task(); } catch (err) { Swal.fire({ icon: 'error', title: 'Could not download', text: errorText(err, 'Please try again.') }); }
    setBusy('');
  };

  const downloadAll = () => run('all', () => downloadZip(
    sectionTitle,
    sectionTitle,
    docs.filter((d) => d.current).map((d) => ({ path: `${d.name}.${extOf(d.current.originalName)}`, versionId: d.current.id })),
  ));

  const downloadHistory = (doc) => run(`h${doc.id}`, () => downloadZip(
    doc.name,
    doc.name,
    doc.versions.map((v, i) => ({ path: `v${doc.versions.length - i} - ${v.originalName}`, versionId: v.id })),
  ));

  const removeDocument = async (doc) => {
    const extra = doc.versions.length > 1 ? ` All ${doc.versions.length} versions` : ' The file';
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete this document?',
      text: `“${doc.name}”.${extra} will be permanently deleted from storage.`,
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#dc2626',
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`${DOCUMENTS_API}/${doc.id}`);
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not delete', text: errorText(err, 'Please try again.') });
    }
  };

  const removeVersion = async (doc, version, number) => {
    const only = doc.versions.length === 1;
    const result = await Swal.fire({
      icon: 'warning',
      title: only ? 'Delete this document?' : `Delete version ${number}?`,
      text: only
        ? `This is the only file of “${doc.name}”, so the whole document will be deleted.`
        : `${version.originalName} will be permanently deleted from storage.${doc.current.id === version.id ? ' The previous version becomes the current one.' : ''}`,
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#dc2626',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await axiosInstance.delete(`${DOCUMENTS_API}/versions/${version.id}`);
      if (res.data.data) setDocs((prev) => prev.map((d) => (d.id === doc.id ? res.data.data : d)));
      else setDocs((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not delete', text: errorText(err, 'Please try again.') });
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900">{heading} ({docs.length})</h3>
          <p className="text-sm text-slate-500">{intro || 'Upload, preview and download the files for this area. Re-uploading keeps the earlier files in the history.'}</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={downloadAll} disabled={docs.length === 0 || Boolean(busy)} className={`${btn} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}>
            {busy === 'all' ? 'Preparing…' : 'Download all (ZIP)'}
          </button>
          <button type="button" onClick={() => setUpload({ mode: 'new' })} disabled={Boolean(disabledReason)} className={btnPrimary}>+ Upload document</button>
        </div>
      </div>

      {disabledReason ? <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">{disabledReason}</p> : null}

      {loadError ? <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{loadError}</p> : null}

      {loading ? (
        <div className="space-y-3">{[0, 1].map((i) => <div key={i} className="h-28 animate-pulse rounded-2xl border border-slate-200 bg-white" />)}</div>
      ) : docs.length === 0 && !loadError ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
          <p className="text-sm font-semibold text-slate-900">No documents uploaded yet</p>
          <p className="mt-1 text-sm text-slate-500">Upload an image, PDF, Word or Excel file to keep it with this area.</p>
          {disabledReason ? null : <button type="button" onClick={() => setUpload({ mode: 'new' })} className={`${btnPrimary} mt-4`}>+ Upload document</button>}
        </div>
      ) : (
        <ul className="space-y-3">
          {docs.map((doc) => {
            const current = doc.current;
            const historyOpen = openHistory.has(doc.id);
            return (
              <li key={doc.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <div className="flex items-start gap-3">
                  <KindBadge fileName={current?.originalName} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="break-words text-sm font-semibold text-slate-900 sm:text-base">{doc.name}</h4>
                      <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700 ring-1 ring-inset ring-sky-200">v{doc.versions.length}</span>
                    </div>
                    {doc.description ? <p className="mt-1 whitespace-pre-line break-words text-sm text-slate-600">{doc.description}</p> : null}
                    {current ? (
                      <p className="mt-1.5 break-words text-xs text-slate-500">
                        {current.originalName} · {formatSize(current.size)} · {current.uploadedBy ? `by ${current.uploadedBy} · ` : ''}{formatWhen(current.uploadedAt)}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {current ? (
                    <>
                      <button type="button" onClick={() => setPreview({ title: doc.name, version: current })} className={btnPrimary}>Preview</button>
                      <button type="button" disabled={Boolean(busy)} onClick={() => run(`d${current.id}`, () => downloadVersion(current))} className={btnNeutral}>
                        {busy === `d${current.id}` ? 'Downloading…' : 'Download'}
                      </button>
                    </>
                  ) : null}
                  <button type="button" onClick={() => setUpload({ mode: 'version', doc })} disabled={Boolean(disabledReason)} className={btnNeutral}>Upload new version</button>
                  <button type="button" onClick={() => toggleHistory(doc.id)} aria-expanded={historyOpen} className={btnNeutral}>
                    {historyOpen ? 'Hide history' : `Upload history (${doc.versions.length})`}
                  </button>
                  <button type="button" onClick={() => setEditing(doc)} className={btnNeutral}>Edit</button>
                  <button type="button" onClick={() => removeDocument(doc)} className={btnDanger}>Delete</button>
                </div>

                {historyOpen ? (
                  <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-2.5">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Upload history · newest first</p>
                      {doc.versions.length > 1 ? (
                        <button type="button" disabled={Boolean(busy)} onClick={() => downloadHistory(doc)} className={`${btn} border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50`}>
                          {busy === `h${doc.id}` ? 'Preparing…' : 'Download all versions (ZIP)'}
                        </button>
                      ) : null}
                    </div>
                    <ul className="divide-y divide-slate-200">
                      {doc.versions.map((v, i) => {
                        const number = doc.versions.length - i;
                        return (
                          <li key={v.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                            <div className="flex min-w-0 items-center gap-3">
                              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${i === 0 ? 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200' : 'bg-white text-slate-500 ring-1 ring-inset ring-slate-200'}`}>
                                v{number}{i === 0 ? ' · current' : ''}
                              </span>
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-slate-800">{v.originalName}</p>
                                <p className="text-xs text-slate-500">{formatSize(v.size)} · {v.uploadedBy ? `${v.uploadedBy} · ` : ''}{formatWhen(v.uploadedAt)}</p>
                              </div>
                            </div>
                            <div className="flex gap-1.5">
                              <button type="button" onClick={() => setPreview({ title: `${doc.name} (v${number})`, version: v })} className={btnNeutral}>Preview</button>
                              <button type="button" disabled={Boolean(busy)} onClick={() => run(`d${v.id}`, () => downloadVersion(v))} className={btnNeutral}>
                                {busy === `d${v.id}` ? '…' : 'Download'}
                              </button>
                              <button type="button" onClick={() => removeVersion(doc, v, number)} className={btnDanger}>Delete</button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {upload ? (
        <UploadModal
          section={section}
          mode={upload.mode}
          doc={upload.doc}
          onClose={() => setUpload(null)}
          onSaved={(doc) => {
            upsert(doc);
            if (upload.mode === 'version') setOpenHistory((prev) => new Set(prev).add(doc.id));
            setUpload(null);
          }}
        />
      ) : null}
      {editing ? <EditModal doc={editing} onClose={() => setEditing(null)} onSaved={(doc) => { setDocs((prev) => prev.map((d) => (d.id === doc.id ? doc : d))); setEditing(null); }} /> : null}
      {preview ? <PreviewModal title={preview.title} version={preview.version} onClose={() => setPreview(null)} /> : null}
    </section>
  );
}
